import './ProgressTab.scss';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@trackman/web-shared-components';
import NoClassesFound from 'components/Organization/NoClassesFound/NoClassesFound';
import { useServices } from 'index';
import { useEffect, useState } from 'react';
import { SelectModule } from './SelectModule/SelectModule';
import { ModuleProgress } from './ModuleProgress/ModuleProgress';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';
import { useTranslation } from 'react-i18next';
import Subtitle from 'components/TextComps/Subtitle';
import { SelectClass } from '../SelectClass/SelectClass';
import { StudentsSearchInput } from '../StudentsSearch/StudentsSearch';

export const ProgressTab = () => {
  const { t } = useTranslation();

  const learningHubService = useServices().learningHubService;
  const classesService = useServices().classesService;

  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);
  const selectedClassId = useOrganizationStore((state) => state.selectedClassId);
  const setSelectedClassId = useOrganizationStore((state) => state.setSelectedClassId);
  const [selectedModuleId, setSelectedModuleId] = useState<string>('');
  const [selectedProgressOverviewIndex, setSelectedProgressOverviewIndex] = useState<number>(0);
  const [searchStudentsInputValue, setSearchStudentsInputValue] = useState<string>('');

  const { data: moduleList, isPending } = useQuery({
    queryKey: ['learningHubModuleList'],
    queryFn: learningHubService.getModules,
    refetchOnWindowFocus: false,
  });

  const { data: classesList, isPending: isClassesListPending } = useQuery({
    queryKey: ['classesList', selectedOrganizationId],
    queryFn: () => classesService.getClasses(selectedOrganizationId, false),
    enabled: !!selectedOrganizationId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (classesList && classesList.length > 0) {
      setSelectedClassId(classesList[0].id);
    }
  }, [classesList]);

  useEffect(() => {
    if (moduleList && moduleList.length > 0) {
      setSelectedModuleId(moduleList[0].id);
    }
  }, [moduleList]);

  return (
    <>
      {isPending && !selectedModuleId ? (
        <div className='loader-wrapper'>
          <Loader size='large' />
        </div>
      ) : (
        <>
          {!classesList?.length ? (
            <NoClassesFound />
          ) : (
            <>
              <div className='module-progress-header'>
                <Subtitle>{t('StudentAndClassProgress', { ns: 'organizationPage' })}</Subtitle>
                <div className='module-progress-filters'>
                  <SelectClass
                    onChange={(newValue) => setSelectedClassId(newValue)}
                    value={selectedClassId ?? ''}
                    classesList={classesList ?? []}
                  />
                  <SelectModule
                    moduleList={moduleList ?? []}
                    selectedModuleId={selectedModuleId}
                    selectModule={(moduleId) => setSelectedModuleId(moduleId)}
                  />
                  {selectedProgressOverviewIndex === 0 && (
                    <StudentsSearchInput
                      label={t('SearchForStudents', { ns: 'organizationPage' }) as string}
                      value={searchStudentsInputValue}
                      onChange={(newValue) => setSearchStudentsInputValue(newValue)}
                    />
                  )}
                </div>
              </div>

              <ModuleProgress
                selectedClassId={selectedClassId}
                selectedModuleId={selectedModuleId}
                selectedModuleName={moduleList?.find((module) => module.id === selectedModuleId)?.title ?? ''}
                isModuleProgressQueryEnabled={!!selectedClassId && !!selectedModuleId}
                selectedProgressOverviewIndex={selectedProgressOverviewIndex}
                setSelectedProgressOverviewIndex={setSelectedProgressOverviewIndex}
                studentsFilter={searchStudentsInputValue}
              />
            </>
          )}
        </>
      )}
    </>
  );
};
